import PhotoGrayIcon from '@assets/images/common/icon/photo-gray.svg';
import { ERROR_MESSAGES } from '@src/utils/errorMessages';
import { useCallback, useEffect, useState } from 'react';

import { ErrorMessage } from '.';

interface FileUploadProps {
  onFileChange?: (file: File | null) => void;
  defaultPreview?: string;
  accept?: string;
  maxSize?: number; // in bytes
  className?: string;
  previewAltText?: string;
  uploadButtonText?: string;
  removeButtonText?: string;
  onDelete?: () => Promise<void>;
}

const DEFAULT_MAX_SIZE = 5 * 1024 * 1024; // Default max file size is 5MB

const FileUpload = ({
  onFileChange,
  defaultPreview = '',
  accept = 'image/*',
  maxSize = DEFAULT_MAX_SIZE,
  className = '',
  previewAltText = 'Preview',
  uploadButtonText = 'アップロードする',
  removeButtonText = '削除する',
  onDelete,
}: FileUploadProps) => {
  const [preview, setPreview] = useState(defaultPreview);
  const [error, setError] = useState('');
  const [isDefaultPreview, setIsDefaultPreview] = useState(false);

  useEffect(() => {
    if (defaultPreview) {
      setPreview(defaultPreview);
      setIsDefaultPreview(true);
    }
  }, [defaultPreview]);

  const createPreviewUrl = useCallback(async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  const validateFile = useCallback(
    (file: File): string => {
      const allowedExtensions = ['jpeg', 'jpg', 'png', 'gif'];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        return ERROR_MESSAGES.FILE.INVALID_FORMAT;
      }
      if (file.size > maxSize) {
        return `サムネイル画像 は ${maxSize / (1024 * 1024)} MB以下のファイルを設定してください。`;
      }
      return '';
    },
    [maxSize],
  );

  const handleFileProcess = useCallback(
    async (file: File | null) => {
      setError('');

      if (!file) {
        setPreview('');
        onFileChange?.(null);
        return;
      }

      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      try {
        const previewUrl = await createPreviewUrl(file);
        setPreview(previewUrl);
        await onFileChange?.(file);
      } catch (err) {
        console.error('File processing error:', err);
      }
    },
    [onFileChange, validateFile, createPreviewUrl],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const file = e.dataTransfer.files[0];
      handleFileProcess(file);
    },
    [handleFileProcess],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] || null;
      handleFileProcess(file);
      e.target.value = '';
    },
    [handleFileProcess],
  );

  const handleRemove = useCallback(async () => {
    try {
      await onDelete?.();
      setPreview('');
      setError('');
      onFileChange?.(null);
    } catch (err) {
      console.error('File deletion error:', err);
    }
  }, [onFileChange, onDelete]);

  return (
    <div className={`w-full ${className}`}>
      <div className='flex flex-col'>
        <div
          className='relative aspect-video w-full overflow-hidden rounded-lg bg-gray-100'
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          role='presentation'
        >
          {preview ? (
            <img
              src={preview}
              alt={previewAltText}
              className='size-full object-contain'
            />
          ) : (
            <div className='absolute inset-0 flex flex-col items-center justify-center text-gray-400'>
              <img
                src={PhotoGrayIcon}
                alt='default-image'
                width='42'
                height='42'
                loading='lazy'
                data-testid={isDefaultPreview ? 'default-preview' : 'default-image'}
              />
            </div>
          )}

          <input
            id='file-upload'
            type='file'
            accept={accept}
            onChange={handleChange}
            className='absolute inset-0 size-full cursor-pointer opacity-0'
            aria-label={uploadButtonText}
            data-testid='file-input'
          />
        </div>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <div className='mt-4 flex items-baseline justify-start gap-4'>
          <label
            htmlFor='file-upload'
            className='cursor-pointer text-xs text-primary-500 underline hover:no-underline'
          >
            <span>{preview ? '変更する' : uploadButtonText}</span>
          </label>

          {preview && (
            <button
              onClick={handleRemove}
              className='cursor-pointer text-xs text-primary-500 underline hover:no-underline'
              type='button'
            >
              {removeButtonText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileUpload;
